import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, Page } from "@/components/shared";

const ROLE_DEST = { masquerader: "/app", band_leader: "/leader", brand_partner: "/brand", admin: "/admin" };

export default function Auth({ mode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const band = loc.state?.band || null;
  const { login, register } = useAuth();
  const isReg = mode === "register";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let user;
      if (isReg) {
        user = await register({ name, email, password, role: "masquerader", band_code: band?.code });
        toast.success("Welcome to Carnival Safety 101!");
      } else {
        user = await login(email, password);
        toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
      }
      nav(ROLE_DEST[user.role] || "/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally { setLoading(false); }
  };

  const demo = async (em) => {
    setLoading(true);
    try {
      const user = await login(em, "carnival123");
      nav(ROLE_DEST[user.role] || "/app", { replace: true });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Page className="cs-shell grain px-6 pt-6 pb-12">
      <button data-testid="auth-back-button" onClick={() => nav(isReg ? "/join" : "/")} className="cs-tap flex items-center gap-1 text-sm mb-6" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Back
      </button>
      <Logo size="sm" className="mb-8" />

      <h1 className="font-head font-extrabold text-3xl">{isReg ? "Create your account" : "Welcome back"}</h1>
      {isReg && band && (
        <p className="mt-2 text-sm">Joining <span className="font-semibold" style={{ color: "var(--cs-magenta)" }}>{band.name}</span></p>
      )}
      {!isReg && <p className="mt-2 text-base" style={{ color: "var(--cs-fg2)" }}>Sign in to continue.</p>}

      <form onSubmit={submit} className="mt-8 space-y-4">
        {isReg && (
          <Field label="YOUR NAME">
            <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Maya Joseph" className="cs-input" />
          </Field>
        )}
        <Field label="EMAIL">
          <input data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com" className="cs-input" />
        </Field>
        <Field label="PASSWORD">
          <input data-testid="auth-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" className="cs-input" />
        </Field>

        <button data-testid="auth-submit-button" type="submit" disabled={loading}
          className="cs-tap w-full magenta-grad magenta-glow rounded-2xl text-white font-head font-bold text-lg"
          style={{ height: 58 }}>
          {loading ? "Please wait…" : isReg ? "CREATE ACCOUNT" : "SIGN IN"}
        </button>
      </form>

      <p className="text-center text-sm mt-5" style={{ color: "var(--cs-fg2)" }}>
        {isReg ? (
          <>Already have an account? <Link data-testid="auth-switch-login" to="/login" className="font-semibold" style={{ color: "var(--cs-magenta)" }}>Sign in</Link></>
        ) : (
          <>New here? <Link data-testid="auth-switch-register" to="/join" className="font-semibold" style={{ color: "var(--cs-magenta)" }}>Join your band</Link></>
        )}
      </p>

      {!isReg && (
        <div className="mt-8">
          <p className="text-center text-xs font-bold tracking-wide mb-3" style={{ color: "#52525b" }}>TRY A DEMO ACCOUNT</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Masquerader", em: "maya@islandvibes.com", t: "demo-masquerader" },
              { label: "Band Leader", em: "hayden.adore@gmail.com", t: "demo-band-leader" },
              { label: "Brand", em: "partner@brightwater.com", t: "demo-brand" },
              { label: "Admin", em: "admin@carnivalsafety.com", t: "demo-admin" },
            ].map((d) => (
              <button key={d.em} data-testid={`auth-${d.t}`} onClick={() => demo(d.em)}
                className="cs-card cs-tap py-3 text-xs font-semibold">{d.label}</button>
            ))}
          </div>
        </div>
      )}

      <style>{`.cs-input{width:100%;height:54px;border-radius:.75rem;padding:0 1rem;background:var(--cs-card);border:1px solid var(--cs-border);color:#fff;outline:none;font-size:16px}.cs-input:focus{border-color:var(--cs-magenta)}`}</style>
    </Page>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold tracking-wide" style={{ color: "var(--cs-fg2)" }}>{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
